import { queryOptions, useQuery } from "@tanstack/react-query";
import React from "react";
import invariant from "tiny-invariant";
import { z } from "zod";

import { getCompanyFeatures, getDefaultFeatures } from "@/generated/api/users";

import { Failed } from "../components/info/info";
import { planTypeSchema } from "../services/user";

type PlanFeaturesSchema = z.infer<typeof planFeaturesSchema>;

type Props = React.PropsWithChildren<{
  company: undefined | null | { id: number };
}>;

const featureValueSchema = z.object({
  menu: z.boolean(),
  mode: z.union([z.literal("FULL"), z.literal("TRIAL")]),
  planId: z
    .number()
    .nullish()
    .transform((v) => v ?? null),
  planType: planTypeSchema.nullish().transform((v) => v ?? null),
});

const featureKeySchema = z.union([
  z.literal("MARKET_NEWS"),
  z.literal("QUICK_TRADING"),
  z.literal("MARKETPLACE"),
  z.literal("STOCK_EXCHANGE"),
  z.literal("PURCHASE_PRICES"),
  z.literal("STORAGES"),
  z.literal("TRADING"),
  z.literal("TRANSACTIONS_ORDERS"),
  z.literal("YIELD_PREDICTIONS"),
]);

const planFeaturesSchema = z.record(
  featureKeySchema,
  featureValueSchema.optional(),
);

const Context = React.createContext<PlanFeaturesSchema | undefined>(undefined);

const FeatureProvider = ({ children, company }: Props) => {
  const companyFeatures = useQuery({
    // @ts-expect-error company won't be null here anyway since enabled check is applied
    ...companyFeaturesQuery({ companyId: company?.id }),
    enabled: !!company,
  });
  const defaultFeatures = useQuery({
    ...defaultFeaturesQuery(),
    enabled: !company,
  });

  const errors = [companyFeatures.error, defaultFeatures.error];
  const pending =
    (companyFeatures.isPending && !!company) ||
    (defaultFeatures.isPending && !company);
  const features = companyFeatures.data ?? defaultFeatures.data;
  const safeFeatures = React.useMemo(
    () => planFeaturesSchema.safeParse(features),
    [features],
  );

  if (errors.some(Boolean)) {
    return <Failed error={errors.find(Boolean)} />;
  }

  if (pending) {
    return null;
  }

  if (!safeFeatures.success) {
    return <Failed error={safeFeatures.error} />;
  }

  return (
    <Context.Provider value={safeFeatures.data}>{children}</Context.Provider>
  );
};

const useFeatureProvider = () => {
  const ctx = React.useContext(Context);
  invariant(
    ctx,
    // eslint-disable-next-line lingui/no-unlocalized-strings
    "useFeatureProvider only available inside <FeatureProvider /> component",
  );
  return ctx;
};

const companyFeaturesQuery = ({ companyId }: { companyId: number }) =>
  queryOptions({
    queryFn: ({ signal }) => getCompanyFeatures(companyId, { signal }),
    queryKey: ["company", companyId, "features"],
    staleTime: Infinity,
  });

const defaultFeaturesQuery = () =>
  queryOptions({
    queryFn: ({ signal }) => getDefaultFeatures({ signal }),
    queryKey: ["features", "default"],
    staleTime: Infinity,
  });

type Features = z.infer<typeof planFeaturesSchema>;

export { FeatureProvider, useFeatureProvider };
export type { Features };
